
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageHero from '@/components/PageHero';
import CallToActionSection from '@/components/CallToActionSection';
import { Title } from '@/components/Title';
import { Users, ShieldCheck, Lightbulb, TrendingUp, Landmark, LineChart, Scale } from 'lucide-react';

const coreValues = [
  {
    icon: Users,
    title: 'Investor-First Mentality',
    description: 'Your success is our success. We are committed to providing the tools, resources, and support you need to make informed investment decisions.',
  },
  {
    icon: ShieldCheck,
    title: 'Unwavering Integrity',
    description: 'Trust is the cornerstone of our platform. We operate with the highest ethical standards, ensuring transparency and fairness in everything we do.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation-Driven',
    description: 'We are constantly pushing the boundaries of what\'s possible, leveraging technology to create a seamless and powerful investment experience.',
  },
];

const investmentApproaches = [
  {
    id: 'growth',
    name: 'Growth-Oriented',
    icon: TrendingUp,
    title: 'Capital Appreciation Focus',
    description: 'Our growth-oriented strategy targets assets with high potential for capital appreciation. We seek out innovative companies and transformative real estate projects in emerging sectors, aiming for significant long-term returns.',
    details: [
      'Focus on venture capital and private equity.',
      'Emphasis on technology, biotech, and renewable energy sectors.',
      'Higher risk/reward profile suitable for long-term investors.',
    ],
  },
  {
    id: 'income',
    name: 'Income-Focused',
    icon: Landmark,
    title: 'Consistent Cash Flow',
    description: 'The income-focused strategy prioritizes investments that generate steady, predictable cash flow. This includes income-producing real estate, private credit, and dividend-paying private companies.',
    details: [
      'Ideal for investors seeking regular income distributions.',
      'Lower volatility compared to growth strategies.',
      'Includes assets like commercial real estate and secured loans.',
    ],
  },
  {
    id: 'balanced',
    name: 'Balanced Strategy',
    icon: Scale,
    title: 'A Blend of Growth and Income',
    description: 'Our balanced strategy offers a diversified approach, combining the potential for capital growth with the stability of income-generating assets. This creates a resilient portfolio designed to perform across different market cycles.',
    details: [
      'Diversified across multiple asset classes and sectors.',
      'Medium risk profile with a mix of growth and income assets.',
      'Aims for moderate growth with downside protection.',
    ],
  },
];

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState(investmentApproaches[0].id);

  return (
    <div className="bg-white">
      <PageHero
        title="About ALTA-X"
        subtitle="We are a team of financial experts, technologists, and innovators dedicated to democratizing access to alternative investments."
        backgroundImage="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      />

      <main>
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8 }}
              >
                <Title as="h2">Our Mission</Title>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                  ALTA-X was founded on a simple, yet powerful idea: the most promising investment opportunities shouldn't be reserved for the few. For too long, private markets—venture capital, private equity, and real estate—have been opaque, inefficient, and inaccessible to most investors.
                </p>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                  We saw a critical need for a platform that could break down these barriers and empower individuals to build diversified, resilient portfolios. By combining deep industry expertise with a cutting-edge technology platform, we provide curated, institutional-quality deals directly to you.
                </p>
              </motion.div>
              <motion.div
                className="h-96 rounded-2xl overflow-hidden shadow-2xl"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                 <img src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Our Team" className="w-full h-full object-cover" />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.8 }}
            >
              <Title as="h2">Our Core Values</Title>
              <p className="mt-4 text-lg text-slate-600">Our principles guide every decision we make, ensuring we always act in the best interest of our investors.</p>
            </motion.div>
            <div className="mt-16 grid md:grid-cols-3 gap-8">
              {coreValues.map((value, index) => (
                <motion.div 
                  key={value.title} 
                  className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-200/80 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                      <value.icon className="w-8 h-8 text-[hsl(var(--navy))]" />
                  </div>
                  <Title as="h3" className="!text-xl mb-2">{value.title}</Title>
                  <p className="text-slate-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.8 }}
            >
              <Title as="h2">Our Investment Philosophy</Title>
              <p className="mt-4 text-lg text-slate-600">We believe in a disciplined, research-driven approach to investing. Our philosophy is built on three core pillars, allowing you to align your portfolio with your financial goals.</p>
            </motion.div>
            <div className="mt-16 max-w-5xl mx-auto">
              <div className="flex justify-center border-b border-slate-300">
                {investmentApproaches.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${ activeTab === tab.id ? 'text-[hsl(var(--navy))]' : 'text-slate-500 hover:text-slate-800' } flex items-center gap-2 text-lg font-semibold py-4 px-6 relative transition-colors`}
                  >
                    <tab.icon className={`w-5 h-5 transition-colors ${activeTab === tab.id ? 'text-[hsl(var(--navy-light))]' : ''}`} />
                    {tab.name}
                    {activeTab === tab.id && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-1 bg-[hsl(var(--navy))]"
                        layoutId="underline"
                      />
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {investmentApproaches.map((item) =>
                      item.id === activeTab ? (
                        <div key={item.id} className="grid md:grid-cols-2 gap-12 items-center bg-white p-8 rounded-2xl shadow-lg border border-slate-200/80">
                          <div className="space-y-4">
                            <Title as="h3">{item.title}</Title>
                            <p className="text-slate-600">{item.description}</p>
                            <ul className="space-y-3 pt-2">
                              {item.details.map((detail) => (
                                <li key={detail} className="flex items-center gap-3">
                                  <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                  <span className="text-slate-700">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="flex items-center justify-center bg-slate-100 rounded-xl h-64">
                            <LineChart className="w-32 h-32 text-slate-400" />
                          </div>
                        </div>
                      ) : null
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>
      </main>

      <CallToActionSection />
    </div>
  );
};

export default AboutPage;
